import { useState } from "react";
import { ACTIVE_STATUSES, type BuildingDto, type IssueDetail, type IssueSummary } from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { api, ApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { locLabel, locText } from "./filters";

/** "Komt ook elders voor": verdieping/ruimte zonder pin, of aanwijzen op de tekening. */
export function AddLocationDialog({
  building: b,
  issue,
  floorId,
  onClose,
  onSaved,
  onPointOnPlan,
}: {
  building: BuildingDto;
  issue: IssueDetail;
  floorId: string;
  onClose: () => void;
  onSaved: (d: IssueDetail) => void;
  onPointOnPlan: (floorId: string) => void;
}) {
  const [fl, setFl] = useState(floorId || b.floors[0]?.id || "");
  const [room, setRoom] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const floor = b.floors.find((f) => f.id === fl);

  async function save() {
    setBusy(true);
    try {
      const d = await api<IssueDetail>(`/issues/${issue.id}/locations`, {
        method: "POST",
        body: { floorId: fl, roomId: room || null },
      });
      toast(`${issue.code}: plek toegevoegd (${d.locations.length} in totaal)`);
      onSaved(d);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Er ging iets mis");
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Plek toevoegen aan ${issue.code}`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          {floor?.plan && (
            <button type="button" className="btn" onClick={() => onPointOnPlan(fl)}>
              Aanwijzen op tekening
            </button>
          )}
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn primary" disabled={!room || busy} onClick={save}>
            Toevoegen zonder pin
          </button>
        </>
      }
    >
      <div className="grid2">
        <label className="field">
          <span>Verdieping</span>
          <select
            value={fl}
            onChange={(e) => {
              setFl(e.target.value);
              setRoom("");
            }}
          >
            {b.floors.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Ruimte</span>
          <select value={room} onChange={(e) => setRoom(e.target.value)}>
            <option value="">—</option>
            {b.rooms
              .filter((r) => r.floorId === fl)
              .map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
          </select>
        </label>
      </div>
      <p className="hint">
        Aanwijzen op de tekening geeft een pin; je kunt dan meerdere plekken achter elkaar aantikken, ook op andere
        verdiepingen.
      </p>
    </Modal>
  );
}

/** "Bestaande bevinding komt hier ook voor": kies de bevinding. */
export function PickIssueDialog({
  building: b,
  issues,
  loc,
  onClose,
  onSaved,
}: {
  building: BuildingDto;
  issues: IssueSummary[];
  loc: { floorId: string; roomId: string | null; x: number; y: number };
  onClose: () => void;
  onSaved: (d: IssueDetail) => void;
}) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const needle = q.toLowerCase();
  const list = issues
    .filter((i) => ACTIVE_STATUSES.has(i.status))
    .filter((i) => !needle || `${i.code} ${i.title}`.toLowerCase().includes(needle))
    .sort((a, c) => a.priority.localeCompare(c.priority));

  async function pick(i: IssueSummary) {
    setBusy(true);
    try {
      const d = await api<IssueDetail>(`/issues/${i.id}/locations`, { method: "POST", body: loc });
      toast(`${i.code}: plek toegevoegd (${d.locations.length} in totaal)`);
      onSaved(d);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Er ging iets mis");
      setBusy(false);
    }
  }

  return (
    <Modal title="Komt hier ook voor" onClose={onClose} busy={busy}>
      <p className="hint">
        Nieuwe plek: <strong>{locLabel(b, loc)}</strong>. Kies de bevinding die hier ook speelt.
      </p>
      <input
        type="search"
        placeholder="Zoek op ID of titel"
        aria-label="Zoek bevinding"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className="pickrows">
        {list.map((i) => (
          <button type="button" key={i.id} onClick={() => pick(i)} disabled={busy}>
            <span className={`chip prio prio-${i.priority}`}>{i.priority}</span>
            <span className="grow">
              <span className="code small">{i.code}</span> {i.title}
              <span className="hint block">{locText(i, b)}</span>
            </span>
          </button>
        ))}
        {list.length === 0 && <span className="hint">Geen actieve bevindingen gevonden.</span>}
      </div>
    </Modal>
  );
}
